import {
  Box,
  Container,
  InputProps,
  NonCancelableCustomEvent,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import BarRaiserControls from '../workflow-viewer/action-controls/BarRaiserControls';
import Barometer from './Barometer';
import '../../resources/styles/scss/components/content-quality/content-quality.scss';
import { REVIEW_COMMENT_MAX } from '../utils/ContentQualityUtils';
import { QuestionResponse } from '@amzn/awscat-pattern-library-service-model';

interface Props {
  contentId: string;
  workflowId: number;
  draftBarRaiserId: string | undefined;
  idToSequenceMap: Map<string, number>;
  sequenceToResponseMap: Map<number, QuestionResponse>;
  score: number;
  handleReviewComplete: () => void;
  contentMainTechDomain: string;
  isUnreviewedPublishedContent?: boolean;
  unpublishRecommended?: boolean;
  lastOverallComment?: string;
}

const ContentQualityQuestionnaireActionBox: React.FunctionComponent<Props> = (props: Props) => {
  const {
    contentId,
    workflowId,
    draftBarRaiserId,
    idToSequenceMap,
    sequenceToResponseMap,
    score,
    handleReviewComplete,
    contentMainTechDomain,
    isUnreviewedPublishedContent,
  } = props;

  /* States */
  const [reviewComment, setReviewComment] = useState<string>('');

  useEffect(() => {
    if (props.lastOverallComment) {
      setReviewComment(props.lastOverallComment);
    }
  }, [props.lastOverallComment]);

  const onReviewCommentChanged = (e: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    setReviewComment(e.detail.value);
  };

  const scoreText = score < 0 ? '-- / 100' : `${score} / 100`;

  const renderReviewCommentMessage = () => {
    const charactersRemaining = REVIEW_COMMENT_MAX - reviewComment.length;
    return reviewComment.length > REVIEW_COMMENT_MAX ? (
      <StatusIndicator type="warning" colorOverride="red">
        <Box fontSize="body-s" display="inline" color="text-status-error">
          {`Must be less than ${REVIEW_COMMENT_MAX} characters`}
        </Box>
      </StatusIndicator>
    ) : (
      <Box variant="p" color="text-body-secondary" fontSize="body-s">
        {`${charactersRemaining} character${charactersRemaining > 1 ? 's' : ''} remaining`}
      </Box>
    );
  };

  return (
    <Container>
      <SpaceBetween direction="vertical" size="m">
        <div id="questionnaire-barometer">
          <Barometer score={score} />
        </div>
        <Box textAlign="center">
          <Box variant="h3">Technical quality score</Box>
          <Box variant="h3">{scoreText}</Box>
        </Box>
        <SpaceBetween direction="vertical" size="xxs">
          <Textarea
            value={reviewComment}
            onChange={onReviewCommentChanged}
            placeholder={'Enter overall content feedback here'}
            ariaRequired
          />
          {renderReviewCommentMessage()}
        </SpaceBetween>
        <BarRaiserControls
          contentId={contentId}
          workflowId={workflowId}
          draftBarRaiserId={draftBarRaiserId}
          idToSequenceMap={idToSequenceMap}
          sequenceToResponseMap={sequenceToResponseMap}
          score={score}
          reviewComment={reviewComment}
          handleReviewComplete={handleReviewComplete}
          contentMainTechDomain={contentMainTechDomain}
          isUnreviewedPublishedContent={isUnreviewedPublishedContent}
          unpublishRecommended={props.unpublishRecommended}
        />
      </SpaceBetween>
    </Container>
  );
};

export default ContentQualityQuestionnaireActionBox;
